import React from 'react'
import { Button, Dialog, DialogContent, Divider, Grid, IconButton, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useRouteMatch } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { triggerPasswordReset, triggerSetTemporaryPassword } from 'app-support/employees/state/actions'
import { Alert, DialogTitleWithCloseButton, DraggablePaperGenerator, Loader } from '@carsys/ui'
import LockIcon from '@mui/icons-material/Lock'
import CopyIcon from '@mui/icons-material/FileCopy'
import GenerateIcon from '@mui/icons-material/Refresh'
import MailIcon from '@mui/icons-material/Mail'
import { useNotifier } from '@cbgms/base/notifier'
import { useClipboardCopy } from '@cbgms/base/hooks/use-clipboard-copy'
import { formatEmailLink } from '@cbgms/base/utils/helpers'

const dragID = 'change_password_employee_dialog'
const specials = '~!@-#$.,-%='
const numbers = '123456789'
const lowers = 'abcdefghijkmnpqrstuvwxyz'
const uppers = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
const remainder = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnopqrstuvwxyz~!@-#$.,-%='
const requiredCharsSet = [uppers, numbers, lowers, specials, remainder]
const generatePassword = (length = 8, wishlists = requiredCharsSet) => {
  return Array.from(crypto.getRandomValues(new Uint32Array(length)))
    .map((x, i) => {
      const setIndex = i > wishlists.length - 1 ? wishlists.length - 1 : i
      return wishlists[setIndex][x % wishlists[setIndex].length]
    })
    .join('')
}

interface IUserPasswordDialogProps {
  open: boolean
  enablePasswordReset: boolean
  linkedEmail: string
  onClose: () => void
}

export const UserPasswordDialog: React.FC<IUserPasswordDialogProps> = ({ open, onClose, linkedEmail, enablePasswordReset }) => {
  const [executing, setExecuting] = React.useState(false)
  const [temporaryPassword, setTemporaryPassword] = React.useState('')
  const { t } = useTranslation()
  const notifier = useNotifier()
  const dispatch = useDispatch()
  const match = useRouteMatch<{ employeeUUID: string; shopID: string }>()
  const copy = useClipboardCopy()

  const resetEmailPasswordLink = formatEmailLink(
    linkedEmail,
    t('setting_employee.send_mailto_temporary_password_subject'),
    t('setting_employee.send_mailto_temporary_password_body', {
      password: encodeURIComponent(temporaryPassword)
    })
  )

  const handlePasswordReset = async () => {
    setExecuting(true)
    try {
      await dispatch(triggerPasswordReset(match.params.employeeUUID, match.params.shopID))
      notifier.success(t('setting_employee.password_reset_success', { email: linkedEmail }))
    } finally {
      setExecuting(false)
    }
  }

  const handleSetTemporary = async () => {
    setExecuting(true)
    try {
      await dispatch(triggerSetTemporaryPassword(match.params.employeeUUID, match.params.shopID, temporaryPassword))
      notifier.success(t('setting_employee.temporary_password_set', { email: linkedEmail }))
    } finally {
      setExecuting(false)
    }
  }
  const copyTemporaryPassword = () => copy(temporaryPassword).then(() => notifier.info(t('setting_employee.copied_temporary_password')))
  const generateTemporaryPassword = () => setTemporaryPassword(generatePassword())

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      scroll='paper'
      PaperComponent={DraggablePaperGenerator(dragID)}
      data-testid='employee-change-password-dialog'
    >
      <DialogTitleWithCloseButton onClose={onClose} draggableIDHandler={dragID}>
        {t('setting_employee.change_password_dialog_header')}
      </DialogTitleWithCloseButton>

      <DialogContent>
        <Grid container direction='column' rowSpacing={1}>
          <Grid item>
            <Alert severity='info'>
              <Typography variant='body1' textAlign='justify'>
                {t('setting_employee.changing_password_for_employee')} <b>{linkedEmail}</b>
              </Typography>
            </Alert>
          </Grid>
          <Grid item>
            {enablePasswordReset ? (
              <Alert severity='info'>
                <Typography variant='body1' textAlign='justify'>
                  {t('setting_employee.change_password_dialog_description')}
                </Typography>
              </Alert>
            ) : (
              <Alert severity='warning'>
                <Typography>{t('setting_employee.reset_password_warning')}</Typography>
              </Alert>
            )}
          </Grid>
          <Grid item>
            <Grid container direction='row'>
              {enablePasswordReset && (
                <Grid item xs container spacing={1} direction='column'>
                  <Grid item>
                    <Typography gutterBottom variant='h6'>
                      {t('setting_employee.reset_password_description')}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Button
                      variant='outlined'
                      color='secondary'
                      fullWidth
                      disabled={executing}
                      startIcon={<MailIcon />}
                      onClick={handlePasswordReset}
                    >
                      {executing && <Loader loading size={20} />}
                      {t('setting_employee.reset_password')}
                    </Button>
                  </Grid>
                </Grid>
              )}

              {enablePasswordReset && <Divider orientation='vertical' flexItem sx={{ mx: 1 }} />}

              <Grid item xs container spacing={1} direction='column'>
                <Grid item>
                  <Typography gutterBottom variant='h6'>
                    {t('setting_employee.set_temporary_password_description')}
                  </Typography>
                </Grid>
                <Grid item display='flex'>
                  <TextField
                    variant='outlined'
                    size='small'
                    fullWidth
                    value={temporaryPassword}
                    onChange={event => setTemporaryPassword(event.target.value)}
                    label={t('setting_employee.temporary_password_placeholder')}
                  />
                  <IconButton onClick={generateTemporaryPassword} size='small'>
                    <GenerateIcon />
                  </IconButton>
                  <IconButton onClick={copyTemporaryPassword} size='small'>
                    <CopyIcon />
                  </IconButton>
                </Grid>
                <Grid item>
                  <Button
                    variant='outlined'
                    color='secondary'
                    fullWidth
                    disabled={executing || temporaryPassword === ''}
                    startIcon={<LockIcon />}
                    onClick={handleSetTemporary}
                  >
                    {executing && <Loader loading size={20} />}
                    {t('setting_employee.set_temporary_password')}
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant='outlined'
                    color='secondary'
                    fullWidth
                    disabled={executing || temporaryPassword === ''}
                    startIcon={<MailIcon />}
                    href={resetEmailPasswordLink}
                  >
                    {t('setting_employee.send_mailto_temporary_password')}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}

export default UserPasswordDialog
